import { apiFetch, v1Path } from './api';
import type { ProjectDto, StateDto, WorkItemSlim } from './work-types';

export const INITIATIVE_PROJECT_IDENTIFIER = 'INIT';
export const REQUIREMENTS_PROJECT_IDENTIFIER = 'REQ';
export const INITIATIVES_PATH = '/initiatives';

/**
 * The hidden workspace that stores initiatives. It is never a stream people pick:
 * initiatives are opened from the Initiatives page.
 */
export function isInitiativeProject(project: { identifier?: string | null } | null | undefined): boolean {
  return project?.identifier === INITIATIVE_PROJECT_IDENTIFIER;
}

/** The hidden workspace that stores business requirements; they are opened from their initiative. */
export function isRequirementsProject(project: { identifier?: string | null } | null | undefined): boolean {
  return project?.identifier === REQUIREMENTS_PROJECT_IDENTIFIER;
}

/** True for a business requirement's key, e.g. REQ-12. */
export function isRequirementKey(key: string): boolean {
  return key.startsWith(`${REQUIREMENTS_PROJECT_IDENTIFIER}-`);
}

export function initiativePath(initiativeId: string): string {
  return `${INITIATIVES_PATH}/${initiativeId}`;
}

type Workspace = { project: ProjectDto; states: StateDto[] };

export type InitiativeWorkspace = Workspace & { requirements: Workspace };

let workspace: Promise<InitiativeWorkspace> | null = null;

/** The org's Initiatives and Business requirements workspaces; the server creates them on first use and joins the caller. */
export function fetchInitiativeWorkspace(): Promise<InitiativeWorkspace> {
  if (!workspace) {
    workspace = apiFetch<InitiativeWorkspace>(v1Path('/initiatives/workspace'), { method: 'POST' }).catch((err: unknown) => {
      workspace = null;
      throw err;
    });
  }
  return workspace;
}

type InitiativeTarget = { id: string; version: number };

export function archiveInitiative(projectId: string, initiative: InitiativeTarget): Promise<WorkItemSlim> {
  return apiFetch<WorkItemSlim>(v1Path(`/projects/${projectId}/work-items/${initiative.id}/archive`), {
    method: 'POST',
    body: JSON.stringify({ version: initiative.version }),
  });
}

export function unarchiveInitiative(projectId: string, initiative: InitiativeTarget): Promise<WorkItemSlim> {
  return apiFetch<WorkItemSlim>(v1Path(`/projects/${projectId}/work-items/${initiative.id}/unarchive`), {
    method: 'POST',
    body: JSON.stringify({ version: initiative.version }),
  });
}

export async function deleteInitiative(projectId: string, initiativeId: string): Promise<void> {
  await apiFetch(v1Path(`/projects/${projectId}/work-items/${initiativeId}`), { method: 'DELETE' });
}

/** Linked tasks stay in their streams either way; deleting also unlinks them. */
export function deleteInitiativeMessage(key: string, linked: number): string {
  return linked > 0
    ? `Delete ${key}? Its ${linked} linked ${linked === 1 ? 'task stays' : 'tasks stay'} in their streams with no initiative.`
    : `Delete ${key}? This cannot be undone from the list.`;
}

export function linkedTaskCount(rollup: { total: number; cancelled: number } | null | undefined): number {
  return (rollup?.total ?? 0) + (rollup?.cancelled ?? 0);
}

/** Tests only: forget the cached workspace. */
export function resetInitiativeWorkspaceCache(): void {
  workspace = null;
}
